import React, { useMemo } from 'react';

import EChartCommon, {
  getAxisPointer,
} from '@/pages/opinion/components/EChartCommon';
import {
  echartsContent,
  getPercentageInfo,
  handleMentionData,
  supplementSingleData,
} from '@/pages/opinion/utils';
import moment from 'moment';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { formatDateFunc } from '@/pages/opinion/utils/time';
import { reviewsFormat } from '@/pages/opinion/utils/value';
import { useIntl, getLocale } from 'umi';

import { baseColors, extendColors } from '@/pages/opinion/constants';
import { ServerTimeType } from '@/pages/opinion/components/FilterCommon/types';
import { formatTimeXAxisText } from '@/pages/opinion/utils/chart';
import { Justify } from 'tea-component';

interface chartInterFace {
  // chart的标题
  title?: string;
  chartData: any;
  dateType: ServerTimeType;
  startTime: string;
  endTime: string;
  formatText?: (text: string) => string;
}

const colorArr = [...baseColors, ...extendColors];

export default ({
  title,
  chartData,
  dateType,
  startTime,
  endTime,
  formatText = (text: string) => text,
}: chartInterFace) => {
  const { formatMessage } = useIntl();
  const locale = getLocale();

  const [timeArr, data] = useMemo(() => {
    const list = supplementSingleData(startTime, endTime, chartData, dateType);
    const needTime = ['daily', 'weekly'].includes(dateType)
      ? 'days'
      : dateType === 'hourly'
      ? 'hourly'
      : 'year';
    const { timeArr, newArr } = handleMentionData(list, needTime);
    return [timeArr, newArr];
  }, [chartData]);

  // 设置图形配置
  const makeupOptions = () => {
    let newLegendData: Array<any> = [];
    let newCharData: Array<any> = data.map((v: any, i: number) => {
      const color = colorArr[i];
      newLegendData.push({
        name: formatText(v.name),
        icon: 'circle',
        itemStyle: {
          color: color,
        },
      });
      return {
        ...v,
        name: formatText(v.name),
        itemStyle: {
          color: color,
        },
      };
    });

    const option = {
      color: colorArr,
      title: {
        text: title,
        textStyle: {
          fontWeight: '400',
          fontSize: 12,
          color: '#444444',
        },
      },
      tooltip: {
        formatter: (params: any) => {
          let rightTitle = listTimeTitleFunc(params[0]?.axisValue, dateType);

          return formatChartParamsDate({
            params: params,
            dealwithRecords(records, total) {
              return records.map((item) => ({
                ...item,
                share: getPercentageInfo(item.originValue, total),
              }));
            },
            tablesConfigFunc: (records: Array<any>) => [
              {
                showHeader: true,
                records,
                columns: [
                  {
                    key: 'text',
                    header: rightTitle,
                  },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: formatMessage({ id: 'News Mentions' }),
                  },
                  {
                    key: 'share',
                    align: 'right',
                    weight: 'bold',
                    header: formatMessage({ id: 'Share-1' }),
                  },
                ],
              },
            ],
          });
        },
      },
      legend: {
        data: newLegendData,
      },
      axisPointer: getAxisPointer((data) => {
        return formatTimeXAxisText(data.value, dateType);
      }),
      xAxis: {
        axisLabel: {
          formatter: (value: any) => formatTimeXAxisText(value, dateType),
        },
        boundaryGap: ['20%', 0],
        data: timeArr,
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: any) => {
            return echartsContent(value);
          },
        },
      },
      series: newCharData,
    };
    return option;
  };

  const options = useMemo(() => {
    return makeupOptions();
  }, [timeArr, data, locale]);

  return (
    <>
      {chartData.length > 0 ? (
        <Justify
          style={{ marginBottom: 10 }}
          left={
            <span style={{ color: '#838697', fontSize: 12, marginBottom: 10 }}>
              {formatMessage({ id: 'News Mentions' })}
            </span>
          }
        ></Justify>
      ) : null}
      <EChartCommon
        empty={chartData.length === 0}
        options={options}
        height={280}
      ></EChartCommon>
    </>
  );
};
